import { assert } from '../lib/assert-sidecar'
import { queryString } from '../lib/queryString-sidecar'
import { rando } from '../lib/rando-sidecar'
import { chessGameManager } from '../proto/chess/chessGameManager-sidecar'
import { chessUiMessage } from '../proto/chess/ui/chessUiMessage-sidecar'
import type { IMessageWriter } from './messageWriter'
import { messageWriter } from './messageWriter-sidecar'
import { settings } from './settings-sidecar'

export interface IWebsocketExtensions {
  extension_onVersionFail: (latestVersion: string, yourVersion: string) => void
  extension_getLocalServerAddress: () => string
  extension_getRemoteServerAddress: () => string
  extension_onMeJoin: () => void
  extension_onOtherJoin: (multiId: number, name: string) => void
  extension_onOtherLeave: (multiId: number) => void
  extension_sendWorldState: (multiId: number) => void
  extension_onUpdate: () => void
  extension_process: (m: IMessageWriter, skipFrom: boolean) => void
}

let _extensions: IWebsocketExtensions | null = null
function setExtensions(ex: IWebsocketExtensions) {
  _extensions = ex
}

const namespace = 'ws:// '
const watchdogHeartbeatInterval = 5000

const verboseIncoming = false
const verboseOutgoing = false
const verboseHeartbeat = false

let _useRemoteMultiplayer = true
export function getUseRemoteMultiplayer() {
  return _useRemoteMultiplayer
}
export function setUseRemoteMultiplayer(v: boolean) {
  _useRemoteMultiplayer = v
}

let _multiplayerAutoJoin = false
export function getMultiplayerAutoJoin() {
  return _multiplayerAutoJoin
}
export function setMultiplayerAutoJoin(v: boolean) {
  _multiplayerAutoJoin = v
}

let _skipVersionCheck = false
export function setSkipVersionCheck(skip: boolean) {
  _skipVersionCheck = skip
}

interface IOutBuffer {
  stringMessage: string
  mw: IMessageWriter | null
  toMultiId: number
}
const _outBuffer: IOutBuffer[] = []
interface IInBuffer {
  stringMessage: string
  buffer: ArrayBuffer | null
}
const _inBuffer: IInBuffer[] = []

let _isSingleplayer = false
export function setSingleplayer(isSingleplayer: boolean) {
  _isSingleplayer = isSingleplayer
}
export function getIsSingleplayer() {
  return _isSingleplayer
}

let _multiId = -1
export function getMultiId() {
  return _multiId
}

let _hostId = -1
export function getHostId() {
  if (_isSingleplayer) {
    return getMultiId()
  }
  return _hostId
}

let _channel = '-'
export function getChannel() {
  return _channel
}

// let _multiName = 'anon'
export function getMultiName() {
  let playerNameOverride = queryString.getByName('name') || ''
  playerNameOverride.trim()
  if (playerNameOverride) {
    return playerNameOverride.trim()
  }

  return settings.get().multiplayerName
}
export function setMultiName(name: string) {
  settings.update({ multiplayerName: name })
  // _multiName = name
}

let _pingTime = 999
export function getPingTime() {
  return _pingTime
}

let _isHost = false
export function getIsHost() {
  return _isHost || _isSingleplayer
}

export function getConnectionStatus() {
  if (!_ws) {
    return 'D/C'
  }
  if (_ws.readyState === WebSocket.CONNECTING) {
    return 'Connecting...'
  }
  if (_ws.readyState === WebSocket.OPEN) {
    return 'Open'
  }
  if (_ws.readyState === WebSocket.CLOSING) {
    return 'Closing...'
  }
  if (_ws.readyState === WebSocket.CLOSED) {
    return 'Closed'
  }
  return '?' + _ws.readyState
}

export function isStillConnecting() {
  return !_ws || _ws.readyState === WebSocket.CONNECTING
}

let _ws: WebSocket | null = null
let _version: string = ''
let _serverVersion: string = ''
let _hasInvalidVersion = false
export function create(ex: IWebsocketExtensions, version: string) {
  _version = version
  setExtensions(ex)
}

export function tryConnect() {
  assert.notNil(_extensions)
  let address = _extensions.extension_getLocalServerAddress()
  if (_useRemoteMultiplayer) {
    address = _extensions.extension_getRemoteServerAddress()
  }

  console.log(namespace, 'try connect to', address)
  _ws = new WebSocket(address)
  _ws.binaryType = 'arraybuffer'
  // console.log('ws', ws)

  let _pingWatchdogTimeout: any = 0
  function heartbeat() {
    // Use `WebSocket#terminate()`, which immediately destroys the connection,
    // instead of `WebSocket#close()`, which waits for the close timer.
    // Delay should be equal to the interval at which your server
    // sends out pings plus a conservative assumption of the latency.
    if (verboseHeartbeat) {
      console.log(namespace, 'heartbeat')
    }
    if (_pingWatchdogTimeout) {
      clearTimeout(_pingWatchdogTimeout)
    }
    _pingWatchdogTimeout = setTimeout(() => {
      console.log(namespace, 'heartbeat failed')
      assert.notNil(_ws)
      _ws.close()
    }, watchdogHeartbeatInterval + 5000)
  }

  _ws.onopen = (ev) => {
    console.log(namespace, 'connected')
    assert.notNil(_ws)
    // ws.socketData = createSocketData()
    // _ws.send('now is: ' + Date.now())
    heartbeat()

    let tryChan = queryString.getByName('chan') || ''
    let isHost = queryString.getByName('host') != null
    console.log(namespace, tryChan, isHost)
    if (_multiplayerAutoJoin) {
      if (tryChan) {
        _isHost = isHost
        if (!isHost) {
          // Join after host, hopefully
          setTimeout(() => {
            joinChannel(tryChan, isHost, '')
          }, 500)
        } else {
          joinChannel(tryChan, isHost, '')
        }
      }
    }
  }
  _ws.onmessage = (ev) => {
    assert.notNil(_ws)
    if (ev.data instanceof ArrayBuffer) {
      _inBuffer.push({
        stringMessage: '',
        buffer: ev.data,
      })
    } else {
      let sm = '' + ev.data
      if (sm.startsWith('ping|')) {
        // Handle heartbeat right away
        heartbeat()
        _ws.send('pong')
      }
      _inBuffer.push({
        stringMessage: sm,
        buffer: null,
      })
    }
  }

  _ws.onclose = () => {
    console.log(namespace, 'disconnected')
    clearTimeout(_pingWatchdogTimeout)
    if (!_hasInvalidVersion) {
      setTimeout(() => {
        tryConnect()
      }, 2000)
    }
  }
}

export function update() {
  assert.notNil(_extensions)
  _extensions.extension_onUpdate()

  while (_inBuffer.length > 0) {
    let msg = _inBuffer.shift()
    if (msg) {
      onMessage(msg.stringMessage, msg.buffer)
    }
  }

  while (_outBuffer.length > 0) {
    let msg = _outBuffer.shift()
    if (msg) {
      assert.notNil(_ws)
      if (_ws.readyState === WebSocket.OPEN) {
        if (msg.stringMessage) {
          if (verboseOutgoing) {
            console.log(namespace, '=>', msg.stringMessage)
          }
          _ws.send(msg.stringMessage)
        } else if (msg.mw) {
          if (verboseOutgoing) {
            console.log(namespace, 'B=>', msg)
          }
          let arrayBuffer = msg.mw.buffer.slice(0, msg.mw.offset)
          _ws.send(arrayBuffer)
        }
      }
    }
  }
}

function onMessage(stringMessage: string, dataBuffer: ArrayBuffer | null) {
  if (dataBuffer) {
    if (verboseIncoming) {
      console.log(namespace, '<=B', dataBuffer)
      console.log('got array buffer', dataBuffer)
    }
    let m = messageWriter.create(false, dataBuffer)

    assert.notNil(_extensions)
    _extensions.extension_process(m, false)
  } else {
    let args = stringMessage.split('|', 50)

    // console.log(namespace, '<=S', sm)

    if (verboseIncoming) {
      console.log(namespace, '<=S', stringMessage)
      console.log(namespace, 'args', args[0], args[1], args[2])
    }
    if (args[0] === 'ping') {
      _pingTime = parseInt(args[1])
      if (verboseIncoming) {
        console.log(namespace, 'my ping is', _pingTime)
      }
    }

    if (args[0] === 'list') {
      let numPlayers = parseInt(args[1])
      let key = args[2]
      let hasPassword = args[3] === 'p'
      _gameList.push({ key, numPlayers, hasPassword })
    }

    if (args[0] === 'welcome') {
      _serverVersion = args[1]
      _multiId = parseInt(args[2])
      if (verboseIncoming) {
        // console.log(namespace, 'i am welcomed as', _serverVersion, _multiId)
      }
      console.log(
        namespace,
        'i am welcomed as',
        _serverVersion,
        _multiId,
        stringMessage,
      )
      if (!_skipVersionCheck) {
        if (_serverVersion !== _version) {
          _hasInvalidVersion = true
          if (_ws) {
            _ws.close()
          }
          assert.notNil(_extensions)
          _extensions.extension_onVersionFail(_serverVersion, _version)
        }
      }
    }

    if (args[0] === 'server-error') {
      chessGameManager.goMenuMain()
      chessUiMessage.setText(
        'Server error',
        'The server has encountered an error, please try again later',
      )
      chessUiMessage.setOpen(true)
    }

    if (args[0] === 'wrong-password') {
      chessGameManager.goMenuMain()
      chessUiMessage.setText(
        'Wrong password',
        'Please check the password and try again',
      )
      chessUiMessage.setOpen(true)
    }

    if (args[0] === 'you-joined') {
      let clientOrHost = args[1]
      _channel = args[2]
      let _sHostId = args[3]

      let forceHost = queryString.getByName('host') != null

      _isHost = clientOrHost === 'host' || forceHost
      _hostId = parseInt(_sHostId)
      console.log(
        namespace,
        'i joined channel',
        _channel,
        clientOrHost,
        _sHostId,
      )

      assert.notNil(_extensions)
      _extensions.extension_onMeJoin()
    }

    if (args[0] === 'chan') {
      if (args[1] === 'joined') {
        let multiId = parseInt(args[2], 10)
        let name = args[3]
        if (verboseIncoming) {
          console.log(namespace, 'report joined!', multiId, name)
        }

        assert.notNil(_extensions)
        _extensions.extension_onOtherJoin(multiId, name)

        if (getIsHost()) {
          _extensions.extension_sendWorldState(multiId)
        }
      }
      if (args[1] === 'left') {
        let multiId = parseInt(args[2], 10)

        console.log(namespace, 'other left channel', multiId)
        if (verboseIncoming) {
          console.log(namespace, 'report left!', multiId)
        }

        assert.notNil(_extensions)
        _extensions.extension_onOtherLeave(multiId)

        if (multiId === getHostId()) {
          // Host left!
          chessGameManager.goMenuMain()
          chessUiMessage.setText('Game closed', 'Host has closed the game room')
          chessUiMessage.setOpen(true)
          _onLeaveChannel()
        }
      }
    }
  }
}

export function joinChannel(
  channelName: string,
  isHost: boolean,
  password: string,
) {
  console.log(namespace, 'join channel', channelName)
  setSingleplayer(false)
  sendToAll('setname|' + getMultiName())
  sendToAll('join|' + channelName + '|' + password)
}
export function leaveChannel() {
  sendToAll('leave')
  _onLeaveChannel()
  console.log(namespace, 'left channel')
}

function _onLeaveChannel() {
  _channel = '-'
  setSingleplayer(false)
}

export function hostChannel(password: string) {
  setSingleplayer(false)
  sendToAll('setname|' + getMultiName())
  sendToAll('host|' + password)
}

function sendToAll(message: string) {
  if (_isSingleplayer) {
    return
  }
  _outBuffer.push({
    stringMessage: message,
    mw: null,
    toMultiId: 0,
  })
}

function sendMessageToAll(m: IMessageWriter, force: boolean = false) {
  if (_isSingleplayer && !force) {
    return
  }

  _outBuffer.push({
    stringMessage: '',
    mw: m,
    toMultiId: 0,
  })
}

function sendMessageToOne(m: IMessageWriter, multiId: number) {
  if (_isSingleplayer) {
    return
  }

  _outBuffer.push({
    stringMessage: '',
    mw: m,
    toMultiId: multiId,
  })
}

function sendMessageRequest(m: IMessageWriter) {
  if (_isSingleplayer || getIsHost()) {
    messageWriter.rewind(m)
    messageWriter.read16(m)
    assert.notNil(_extensions)
    _extensions.extension_process(m, true)
  } else {
    sendMessageToAll(m)
  }
}

function sendMessageToOthers(m: IMessageWriter) {
  if (_isSingleplayer) {
  } else {
    sendMessageToAll(m)
  }
}

function sendMessageAndImmediatelyExecute(m: IMessageWriter) {
  if (!getIsHost()) {
    // debugger
    console.error('trying to send and execute message but not host')
    messageWriter.rewind(m)
    let a = messageWriter.read8(m)
    let b = messageWriter.read8(m)
    let c = messageWriter.read8(m)
    console.error('trying to send and execute message but not host', a, b, c)
    return
  }
  sendMessageToAll(m)
  messageWriter.rewind(m)
  messageWriter.read16(m)
  assert.notNil(_extensions)
  _extensions.extension_process(m, true)
}

export function sendMessageAndImmediatelyExecute_nonHost(m: IMessageWriter) {
  sendMessageToAll(m)
  messageWriter.rewind(m)
  messageWriter.read16(m)
  assert.notNil(_extensions)
  _extensions.extension_process(m, true)
}

export function _sendMessageRequest<T>(
  write: (m: IMessageWriter, msg: T) => void,
  msg: T,
) {
  let m = messageWriter.create() // TODO: send to host id
  messageWriter.writeHeader(m, 0)
  write(m, msg)
  sendMessageRequest(m)
}

export function _sendMessageToOthers<T>(
  write: (m: IMessageWriter, msg: T) => void,
  msg: T,
) {
  let m = messageWriter.create()
  messageWriter.writeHeader(m, 0)
  write(m, msg)
  sendMessageToOthers(m)
}

export function _sendMessageAndImmediatelyExecute<T>(
  write: (m: IMessageWriter, msg: T) => void,
  msg: T,
) {
  let m = messageWriter.create()
  messageWriter.writeHeader(m, 0)
  write(m, msg)
  sendMessageAndImmediatelyExecute(m)
}

export function _sendMessageAndImmediatelyExecute_nonHost<T>(
  write: (m: IMessageWriter, msg: T) => void,
  msg: T,
) {
  let m = messageWriter.create()
  messageWriter.writeHeader(m, 0)
  write(m, msg)
  sendMessageAndImmediatelyExecute_nonHost(m)
}

export function _sendMessageWorldState<T>(
  write: (m: IMessageWriter, msg: T) => void,
  msg: T,
  multiId: number,
) {
  let m = messageWriter.create()
  messageWriter.writeHeader(m, multiId)
  write(m, msg)
  sendMessageToOne(m, multiId)
  // console.log('writing world state', multiId)
}

interface IGameListItem {
  key: string
  numPlayers: number
  hasPassword: boolean
}
let _gameList: IGameListItem[] = []
export function refreshGameList() {
  _gameList = []
  sendToAll('list|')
}
export function getGameList(): IGameListItem[] {
  // return getGameList_test()
  return _gameList
}
export function getGameList_test(): IGameListItem[] {
  let testGames: IGameListItem[] = []
  for (let i = 0; i < 100; i++) {
    testGames.push({
      key: 'XYZ-' + rando.i(100, 999),
      numPlayers: rando.i(1, 3),
      hasPassword: rando.b(),
    })
  }
  return testGames
}

export function reportBadCommand(
  m: IMessageWriter,
  msg: any,
  ...error: string[]
) {
  console.error('invalid message!', ...error)
}
export function testRequiredFromHost(
  isFromHost: boolean,
  m: IMessageWriter,
  msgId: number,
  ...error: string[]
) {
  if (!isFromHost) {
    console.error('message not from host!', msgId, ...error)
  }
  return !isFromHost
}
